import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analytics.service';
import { ChurnAnalysisResponse, KpiQueryParams, CompanyChurnData, MonthlyCompanyChurnResponse } from '../../types/kpi.types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Loader } from '../Loader';

interface ChurnAnalysisProps {
  queryParams: KpiQueryParams;
}

const formatCurrency = (amount: number): string => {
  if (amount >= 1000000) return `₪${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `₪${(amount / 1000).toFixed(1)}K`;
  return `₪${amount.toFixed(0)}`;
};

const formatMonth = (month: string): string => {
  const [year, m] = month.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m) - 1]} '${year.slice(2)}`;
};

interface ChurnByTransferCount {
  transferCount: number;
  transferCountLabel: string;
  totalUsers: number;
  churnedUsers: number;
  churnPercentage: number;
}

export const ChurnAnalysis: React.FC<ChurnAnalysisProps> = ({ queryParams }) => {
  const [summaryData, setSummaryData] = useState<ChurnAnalysisResponse | null>(null);
  const [monthlyCompanyData, setMonthlyCompanyData] = useState<MonthlyCompanyChurnResponse | null>(null);
  const [churnByTransferCount, setChurnByTransferCount] = useState<ChurnByTransferCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [summary, monthlyCompany, churnByTransfer] = await Promise.all([
          analyticsService.getChurnAnalysis({
            companyId: queryParams.companyId,
            database: queryParams.database,
          }),
          analyticsService.getMonthlyCompanyChurn({
            companyId: queryParams.companyId,
            database: queryParams.database,
          }),
          analyticsService.getChurnRateByTransferCount({
            companyId: queryParams.companyId,
            database: queryParams.database,
          }),
        ]);
        setSummaryData(summary);
        setMonthlyCompanyData(monthlyCompany);
        setChurnByTransferCount(churnByTransfer);
      } catch (err: any) {
        setError(err.message || 'Failed to load churn data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [queryParams.companyId, queryParams.database]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#ff4444', backgroundColor: '#fff', borderRadius: '12px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <div style={{ fontSize: '18px', fontWeight: 600 }}>Error Loading Data</div>
        <div style={{ fontSize: '14px', marginTop: '8px' }}>{error}</div>
      </div>
    );
  }

  if (!summaryData || !monthlyCompanyData) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>No churn data available</div>;
  }

  const { summary, churnSegmentation } = summaryData;
  const { monthlyCompanyData: allMonthlyData, companies } = monthlyCompanyData;

  // Calculate totals across all companies for each month
  const monthlyTotalsMap = new Map<string, {
    month: string;
    totalEmployees: number;
    churnedEmployees: number;
    churnedWithTransfers: number;
    churnedWithoutTransfers: number;
    totalWithTransfers: number;
    totalWithoutTransfers: number;
  }>();

  allMonthlyData.forEach(row => {
    if (!monthlyTotalsMap.has(row.month)) {
      monthlyTotalsMap.set(row.month, {
        month: row.month,
        totalEmployees: 0,
        churnedEmployees: 0,
        churnedWithTransfers: 0,
        churnedWithoutTransfers: 0,
        totalWithTransfers: 0,
        totalWithoutTransfers: 0,
      });
    }
    const totals = monthlyTotalsMap.get(row.month)!;
    totals.totalEmployees += row.totalEmployees;
    totals.churnedEmployees += row.churnedEmployees;
    totals.churnedWithTransfers += row.churnedWithTransfers;
    totals.churnedWithoutTransfers += row.churnedWithoutTransfers;
    totals.totalWithTransfers += row.totalWithTransfers;
    totals.totalWithoutTransfers += row.totalWithoutTransfers;
  });

  const monthlyTotals = Array.from(monthlyTotalsMap.values())
    .map(totals => {
      const churnRate = totals.totalEmployees > 0 
        ? Math.round((totals.churnedEmployees / totals.totalEmployees) * 100) 
        : 0;
      const churnRateWithTransfers = totals.totalWithTransfers > 0 
        ? Math.round((totals.churnedWithTransfers / totals.totalWithTransfers) * 100) 
        : 0;
      const churnRateWithoutTransfers = totals.totalWithoutTransfers > 0 
        ? Math.round((totals.churnedWithoutTransfers / totals.totalWithoutTransfers) * 100) 
        : 0;

      return {
        ...totals,
        churnRate,
        churnRateWithTransfers,
        churnRateWithoutTransfers,
      };
    })
    .sort((a, b) => a.month.localeCompare(b.month));

  // Filter data based on global company selector
  const displayData = !queryParams.companyId
    ? monthlyTotals
    : allMonthlyData.filter(row => row.companyId.toString() === queryParams.companyId);

  const displayLabel = !queryParams.companyId
    ? 'All Companies (Total)' 
    : companies.find(c => c.companyId.toString() === queryParams.companyId)?.companyName || 'Company';

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#333' }}>
          👥 Employee Turnover Analysis
        </h2>
        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
          Tracks employees who stopped working at client companies (based on shift data) • This is <strong>company-level turnover</strong>, not Payro app usage
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        {/* Total Employees */}
        <div style={{
          backgroundColor: '#fff',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          borderLeft: '4px solid #2196F3',
        }}>
          <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Total Workforce
          </div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#2196F3', marginTop: '8px' }}>
            {summary.totalEmployeesWithShifts.toLocaleString()}
          </div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
            employees with any shift history
          </div>
        </div>

        {/* Active */}
        <div style={{
          backgroundColor: '#fff',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          borderLeft: '4px solid #4CAF50',
        }}>
          <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            ✅ Still at the Company
          </div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#4CAF50', marginTop: '8px' }}>
            {summary.activeEmployees.toLocaleString()}
          </div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
            had a shift in the last 60 days
          </div>
        </div>

        {/* Churned */}
        <div style={{
          backgroundColor: '#fff',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          borderLeft: '4px solid #f44336',
        }}>
          <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            ❌ Left the Company
          </div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#f44336', marginTop: '8px' }}>
            {summary.churnedEmployees.toLocaleString()}
          </div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
            no shift in 60+ days ({summary.churnRate}% turnover rate)
          </div>
        </div>
      </div>

      {/* Segmentation Cards - Churned WITH vs WITHOUT Transfers */}
      <div style={{
        backgroundColor: '#fff',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        marginBottom: '24px',
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
          🎯 Of Employees Who Left — Were They Payro Users?
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Churned WITH Transfers */}
          <div style={{
            backgroundColor: '#FFF3E0',
            padding: '20px',
            borderRadius: '12px',
            border: '2px solid #FF9800',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <span style={{ fontSize: '24px' }}>💸</span>
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#E65100' }}>
                Who Used Payro
              </span>
            </div>
            <div style={{ fontSize: '36px', fontWeight: 700, color: '#E65100' }}>
              {churnSegmentation.churnedWithTransfers.toLocaleString()}
            </div>
            <div style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>
              Left the company — had used Payro at least once
            </div>
            <div style={{
              marginTop: '12px',
              padding: '10px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              fontSize: '14px',
            }}>
              <strong>Turnover Rate:</strong> <span style={{ fontSize: '18px', fontWeight: 700, color: '#E65100' }}>{summary.churnRateWithTransfers}%</span>
            </div>
          </div>

          {/* Churned WITHOUT Transfers */}
          <div style={{
            backgroundColor: '#FFEBEE',
            padding: '20px',
            borderRadius: '12px',
            border: '2px solid #f44336',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <span style={{ fontSize: '24px' }}>🚫</span>
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#C62828' }}>
                Who Never Used Payro
              </span>
            </div>
            <div style={{ fontSize: '36px', fontWeight: 700, color: '#C62828' }}>
              {churnSegmentation.churnedWithoutTransfers.toLocaleString()}
            </div>
            <div style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>
              Left the company — never used Payro
            </div>
            <div style={{
              marginTop: '12px',
              padding: '10px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              fontSize: '14px',
            }}>
              <strong>Turnover Rate:</strong> <span style={{ fontSize: '18px', fontWeight: 700, color: '#C62828' }}>{summary.churnRateWithoutTransfers}%</span>
            </div>
          </div>
        </div>
        
        {/* Explanation */}
        <div style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: '#F5F5F5',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#666',
        }}>
          <strong>📖 Understanding Turnover Rates:</strong>
          <div style={{ marginTop: '6px', lineHeight: '1.6' }}>
            The churn rate uses a weighted average calculation that accounts for the proportion of each segment in the total registered user base.
            <br /><br />
            <strong> "Who Used Payro"</strong> = (Churned employees who used Payro ÷ Total churned employees) × (Total Payro users ÷ Total registered users) × 100
            <br />
            <strong> "Who Never Used Payro"</strong> = (Churned employees who never used Payro ÷ Total churned employees) × (Total non-Payro users ÷ Total registered users) × 100
          </div>
        </div>
      </div>

      {/* Churn Rate by Transfer Count Graph */}
      <div style={{
        backgroundColor: '#fff',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        marginBottom: '24px',
      }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
          📊 Payro Churn Rate by Total Transfer Count
        </h3>
        <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '14px' }}>
          Percentage of users who became Payro churners (didn't transfer in 90+ days) after making exactly X transfers.
        </p>
        {churnByTransferCount.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={churnByTransferCount} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey="transferCountLabel" 
                label={{ value: 'Total Transfers', position: 'insideBottom', offset: -5 }}
                stroke="#666"
              />
              <YAxis 
                label={{ value: '% Churned', angle: -90, position: 'insideLeft' }}
                domain={[0, 100]}
                tickFormatter={(tick) => `${tick}%`}
                stroke="#666"
              />
              <Tooltip 
                formatter={(value: any, name: any, props: any) => [
                  `${value.toFixed(1)}%`,
                  '% Churned'
                ]}
                labelFormatter={(label) => `${label} transfers`}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="churnPercentage" 
                stroke="#D9534F" 
                name="% Churned" 
                strokeWidth={2}
                activeDot={{ r: 6 }} 
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
            No data available
          </div>
        )}
        <div style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: '#F5F5F5',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#666',
        }}>
          <strong>📖 Understanding This Analysis:</strong>
          <div style={{ marginTop: '6px', lineHeight: '1.6' }}>
            This graph shows the churn rate for users segmented by their total lifetime transfer count. 
            A user is considered a "Payro churner" if their last transfer was 90+ days ago. 
            For example, if 100 users made exactly 5 transfers, and 60 of them haven't transferred in 90+ days, 
            the churn rate for "5 transfers" would be 60%.
          </div>
        </div>
      </div>

      {/* Monthly Churn Table */}
      <div style={{
        backgroundColor: '#fff',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            📅 Monthly Turnover Analysis - {displayLabel}
          </h3>
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#E3F2FD', 
            borderRadius: '8px',
            fontSize: '13px',
            color: '#1565C0',
          }}>
            <strong>📖 Note:</strong> 
            <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
              <li style={{ marginBottom: '6px' }}>
                <strong>"Total Employees"</strong> counts all employees who were active (had at least one shift) up to and including that month.
              </li>
              <li style={{ marginBottom: '6px' }}>
                <strong>"Churned (in month)"</strong> shows employees whose last shift was in that month and have now churned (60+ days since last shift).
              </li>
              <li style={{ marginBottom: '6px' }}>
                <strong>"Churn % (Used)"</strong> = (Churned employees who used Payro in that month ÷ Total churned employees in that month) × (Total Payro users ÷ Total registered users) × 100
              </li>
              <li>
                <strong>"Churn % (Never)"</strong> = (Churned employees who never used Payro in that month ÷ Total churned employees in that month) × (Total non-Payro users ÷ Total registered users) × 100
              </li>
            </ul>
          </div>
        </div>
        
        {displayData.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
            No monthly churn data available
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>Month</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0' }} title="All employees who had at least one shift up to this month">Active Workforce</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0' }}>❌ Left Company</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0' }}>💸 Were Payro Users</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0' }}>🚫 Never Used Payro</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0' }}>Turnover % (Payro)</th>
                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0' }}>Turnover % (Non-Payro)</th>
                </tr>
              </thead>
              <tbody>
                {displayData.map((row, idx) => (
                  <tr key={!queryParams.companyId ? row.month : `${row.month}-${(row as any).companyId}`} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '12px', borderBottom: '1px solid #eee', fontWeight: 500 }}>
                      {formatMonth(row.month)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                      {row.totalEmployees.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee', color: '#f44336', fontWeight: 600 }}>
                      {row.churnedEmployees.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee', color: '#E65100' }}>
                      {row.churnedWithTransfers.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee', color: '#C62828' }}>
                      {row.churnedWithoutTransfers.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee', color: '#E65100', fontWeight: 500 }}>
                      {row.churnRateWithTransfers}%
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee', color: '#C62828', fontWeight: 500 }}>
                      {row.churnRateWithoutTransfers}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend / Explanation */}
      <div style={{
        marginTop: '24px',
        padding: '16px 20px',
        backgroundColor: '#E3F2FD',
        borderRadius: '12px',
        fontSize: '13px',
        color: '#1565C0',
      }}>
        <strong>📖 How to read this:</strong>
        <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '8px' }}>
          <div><strong>✅ Still at the Company:</strong> Had a shift in the last 60 days</div>
          <div><strong>❌ Left the Company:</strong> No shift for 60+ days — employee turnover, not Payro usage</div>
          <div><strong>💸 Were Payro Users:</strong> Made at least one transfer before leaving</div>
          <div><strong>🚫 Never Used Payro:</strong> Left without ever making a transfer</div>
        </div>
      </div>
    </div>
  );
};

